/**
 * PATH: packages/ui/src/components/settings/settings-tab-sections/preferences-sections.tsx
 * PURPOSE: Build the "Preferences" tab sections (Appearance + Nav + Notifications + Email + Files + Keyboard)
 * WHY: Extracted from buildSettingsTabs.tsx to keep files under 300 lines
 *
 * FLOW:
 *   ┌─────────────────────────┐     ┌────────────────────┐
 *   │ BuildSettingsTabsOptions │ → │ SettingsSection[]   │
 *   └─────────────────────────┘     └────────────────────┘
 *
 * DEPENDENCIES:
 *   - AppearanceTab, NavigationTab, NotificationsTab, EmailTab, FilesTab, KeyboardShortcutsTab
 * RELATED:
 *   - buildSettingsTabs.tsx: consumes this builder
 */

import { Palette, LayoutGrid, Bell, Mail, Folder, Keyboard } from 'lucide-react'
import { AppearanceTab } from '../AppearanceTab'
import { NavigationTab, type NavigationTabProps } from '../NavigationTab'
import { NotificationsTab, type NotificationsTabProps } from '../NotificationsTab'
import { EmailTab, type EmailTabProps } from '../EmailTab'
import { FilesTab, type FilesTabProps } from '../FilesTab'
import { KeyboardShortcutsTab } from '../KeyboardShortcutsTab'
import { AllKanbanSettingsTabSlot } from '../AllKanbanSettingsTabSlot'
import type { SettingsSection } from '../SectionedContent'
import type { HubId } from '../../types'
import type { BuildSettingsTabsOptions } from '../buildSettingsTabs'

/**
 * buildPreferencesSections — Appearance, Navigation, Notifications, Email, Files, Keyboard
 * Accepts the full options object; extracts only what it needs.
 */
export function buildPreferencesSections(
  options: BuildSettingsTabsOptions,
): SettingsSection[] {
  const {
    hubId,
    hubName,
    apiClient,
    organisationId,
    navigationProps,
    notificationsProps,
    emailProps,
    filesProps,
    shortcuts,
    showUniversalShortcuts = true,
    keyboardContent,
    user,
  } = options

  const resolvedNavigationProps: NavigationTabProps = {
    currentHub: hubId as HubId | undefined,
    apiClient,
    organisationId,
    ...navigationProps,
  }

  const resolvedNotificationsProps: NotificationsTabProps = {
    apiClient,
    ...notificationsProps,
  }

  const resolvedEmailProps: EmailTabProps = {
    apiClient,
    ...emailProps,
  }

  const resolvedFilesProps: FilesTabProps = {
    apiClient,
    ...filesProps,
  }

  return [
    {
      id: 'appearance',
      title: 'Appearance',
      icon: Palette,
      description: 'Theme, colors, and density',
      content: <AppearanceTab />,
    },
    {
      id: 'navigation',
      title: 'Navigation',
      icon: LayoutGrid,
      description: 'Sidebar layout and quick access links',
      content: <NavigationTab {...resolvedNavigationProps} />,
      defaultCollapsed: true,
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: Bell,
      description: 'Push and email notification preferences',
      content: <NotificationsTab {...resolvedNotificationsProps} />,
      defaultCollapsed: true,
    },
    {
      id: 'email-prefs',
      title: 'Email Preferences',
      icon: Mail,
      description: 'Default signatures and send behaviour',
      content: <EmailTab {...resolvedEmailProps} />,
      defaultCollapsed: true,
    },
    {
      id: 'files',
      title: 'Files & Storage',
      icon: Folder,
      description: 'Upload defaults and visibility',
      content: <FilesTab {...resolvedFilesProps} />,
      defaultCollapsed: true,
    },
    {
      id: 'keyboard',
      title: 'Keyboard Shortcuts',
      icon: Keyboard,
      description: 'All available hotkeys',
      content: keyboardContent || (
        <KeyboardShortcutsTab
          shortcuts={shortcuts}
          showUniversal={showUniversalShortcuts}
          hubName={hubName}
        />
      ),
      defaultCollapsed: true,
    },
    {
      id: 'all-kanban',
      title: 'All Kanban',
      icon: LayoutGrid,
      description: 'Default card face, drawer, and Kanban rules',
      content: (
        <AllKanbanSettingsTabSlot
          userId={user?.id ?? null}
          organisationId={organisationId}
        />
      ),
    },
  ]
}
